import { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import {
  Calendar, Clock, Home, CheckCircle, AlertCircle,
  Loader2, Phone, CalendarPlus
} from "lucide-react";
import { API } from "@/App";

export const BookInspectionPage = () => {
  const { token } = useParams();
  
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [booking, setBooking] = useState(null);
  const [error, setError] = useState(null);
  const [submitted, setSubmitted] = useState(false);
  
  const [responseType, setResponseType] = useState("accept");
  const [selectedDate, setSelectedDate] = useState("");
  const [selectedTime, setSelectedTime] = useState("");
  const [alternativeDates, setAlternativeDates] = useState([]);
  const [notes, setNotes] = useState("");

  useEffect(() => {
    fetchBooking();
  }, [token]);

  const fetchBooking = async () => {
    try {
      const response = await axios.get(`${API}/inventory/booking/${token}`);
      setBooking(response.data.booking);
      
      // Pre-select first proposed date
      if (response.data.booking?.proposed_dates?.length > 0) {
        setSelectedDate(response.data.booking.proposed_dates[0]);
      }
    } catch (err) {
      setError(err.response?.data?.detail || "Invalid or expired booking link");
    } finally {
      setLoading(false);
    }
  };

  const addAlternativeDate = (date) => {
    if (date && !alternativeDates.includes(date)) {
      setAlternativeDates([...alternativeDates, date].sort());
    }
  };

  const handleSubmit = async () => {
    if (responseType === "accept" && !selectedDate) {
      toast.error("Please select a date");
      return;
    }
    if (responseType === "propose_alternative" && alternativeDates.length === 0) {
      toast.error("Please propose at least one alternative date");
      return;
    }

    setSubmitting(true);
    try {
      await axios.post(`${API}/inventory/booking/${token}/respond`, {
        response_type: responseType,
        selected_date: selectedDate,
        selected_time: selectedTime,
        alternative_dates: alternativeDates,
        notes: notes
      });
      
      setSubmitted(true);
      toast.success("Response submitted successfully");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to submit response");
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-purple-50 to-slate-100">
        <Loader2 className="w-8 h-8 animate-spin text-purple-600" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-red-50 to-slate-100 p-4">
        <Card className="max-w-md w-full">
          <CardContent className="py-12 text-center">
            <AlertCircle className="w-16 h-16 text-red-500 mx-auto mb-4" />
            <h2 className="text-xl font-bold text-red-700 mb-2">Unable to Load Booking</h2>
            <p className="text-muted-foreground">{error}</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (submitted) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-green-50 to-slate-100 p-4">
        <Card className="max-w-md w-full">
          <CardContent className="py-12 text-center">
            <CheckCircle className="w-16 h-16 text-green-500 mx-auto mb-4" />
            <h2 className="text-2xl font-bold text-green-700 mb-2">
              {responseType === "accept" ? "Inspection Confirmed!" : "Response Submitted!"}
            </h2>
            <p className="text-muted-foreground mb-4">
              {responseType === "accept" 
                ? `Your inspection is booked for ${new Date(selectedDate).toLocaleDateString('en-GB', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' })}${selectedTime ? ` at ${selectedTime}` : ''}.`
                : "We'll be in touch shortly with alternative dates."}
            </p>
            <p className="text-sm text-muted-foreground">
              You can close this window now.
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-50 to-slate-100 py-8 px-4">
      <div className="max-w-lg mx-auto">
        {/* Header */}
        <div className="text-center mb-8">
          <h1 className="text-2xl font-bold text-slate-800">Goodacres Residential</h1>
          <p className="text-muted-foreground">Property Inspection Booking</p>
        </div>

        {/* Property Card */}
        <Card className="mb-6">
          <CardHeader className="bg-gradient-to-r from-purple-600 to-purple-700 text-white rounded-t-lg">
            <CardTitle className="flex items-center gap-2">
              <Home className="w-5 h-5" />
              {booking?.inspection_type?.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase())} Inspection
            </CardTitle>
          </CardHeader>
          <CardContent className="p-6">
            <div className="mb-4 pb-4 border-b">
              <p className="font-semibold text-lg">{booking?.property_address}</p>
            </div>

            {booking?.clerk_notes && (
              <div className="mb-4 p-3 bg-purple-50 rounded-lg">
                <p className="text-sm text-purple-800">{booking.clerk_notes}</p>
              </div>
            )}

            {/* Proposed Dates */}
            <div className="mb-6">
              <Label className="text-base font-semibold mb-3 block">Proposed Dates</Label>
              <div className="space-y-2">
                {booking?.proposed_dates?.map((date) => (
                  <div 
                    key={date}
                    className={`flex items-center justify-between p-3 rounded-lg border-2 cursor-pointer transition-all ${
                      selectedDate === date && responseType === "accept"
                        ? 'border-purple-500 bg-purple-50'
                        : 'border-slate-200 hover:border-purple-300'
                    }`}
                    onClick={() => {
                      setSelectedDate(date);
                      setResponseType("accept");
                    }}
                  >
                    <div className="flex items-center gap-3">
                      <Calendar className="w-5 h-5 text-purple-600" />
                      <div>
                        <p className="font-medium">
                          {new Date(date).toLocaleDateString('en-GB', { 
                            weekday: 'long', 
                            day: 'numeric', 
                            month: 'long' 
                          })}
                        </p>
                      </div>
                    </div>
                    {selectedDate === date && responseType === "accept" && (
                      <CheckCircle className="w-5 h-5 text-purple-600" />
                    )}
                  </div>
                ))}
              </div>
            </div>

            {/* Time Selection */}
            {selectedDate && responseType === "accept" && booking?.proposed_times?.length > 0 && (
              <div className="mb-6">
                <Label className="text-sm font-medium mb-2 block">Preferred Time</Label>
                <div className="flex flex-wrap gap-2">
                  {booking.proposed_times.map((time) => (
                    <Badge
                      key={time}
                      variant={selectedTime === time ? "default" : "outline"}
                      className="cursor-pointer px-3 py-1"
                      onClick={() => setSelectedTime(time)}
                    >
                      <Clock className="w-3 h-3 mr-1" />
                      {time}
                    </Badge>
                  ))}
                </div>
              </div>
            )}

            {/* Alternative Response Options */}
            <div className="border-t pt-4 mt-4">
              <p className="text-sm text-muted-foreground mb-3">
                None of these dates work?
              </p>
              
              <div className="space-y-3">
                <div 
                  className={`p-3 rounded-lg border-2 cursor-pointer ${
                    responseType === "propose_alternative" 
                      ? 'border-amber-500 bg-amber-50' 
                      : 'border-slate-200'
                  }`}
                  onClick={() => setResponseType("propose_alternative")}
                >
                  <div className="flex items-center gap-2">
                    <CalendarPlus className="w-5 h-5 text-amber-600" />
                    <span className="font-medium">Suggest Different Dates</span>
                  </div>
                  
                  {responseType === "propose_alternative" && (
                    <div className="mt-3">
                      <Input
                        type="date"
                        className="mb-2"
                        onChange={(e) => e.target.value && addAlternativeDate(e.target.value)}
                      />
                      <div className="flex flex-wrap gap-2">
                        {alternativeDates.map((date) => (
                          <Badge key={date} variant="secondary">
                            {new Date(date).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })}
                            <span 
                              className="ml-1 cursor-pointer"
                              onClick={(e) => {
                                e.stopPropagation();
                                setAlternativeDates(alternativeDates.filter(d => d !== date));
                              }}
                            >×</span>
                          </Badge>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
                
                <div 
                  className={`p-3 rounded-lg border-2 cursor-pointer ${
                    responseType === "request_callback" 
                      ? 'border-blue-500 bg-blue-50' 
                      : 'border-slate-200'
                  }`}
                  onClick={() => setResponseType("request_callback")}
                >
                  <div className="flex items-center gap-2">
                    <Phone className="w-5 h-5 text-blue-600" />
                    <span className="font-medium">Request a Call Back</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Notes */}
            <div className="mt-4">
              <Label>Additional Notes (Optional)</Label>
              <Textarea
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder="Any access instructions or special requirements..."
                rows={2}
                className="mt-1"
              />
            </div>

            {/* Submit Button */}
            <Button
              onClick={handleSubmit}
              disabled={submitting}
              className="w-full mt-6 h-12 text-lg"
            >
              {submitting ? (
                <Loader2 className="w-5 h-5 animate-spin mr-2" />
              ) : (
                <CheckCircle className="w-5 h-5 mr-2" />
              )}
              {responseType === "accept" 
                ? "Confirm This Date" 
                : responseType === "propose_alternative"
                  ? "Submit Alternative Dates"
                  : "Request Call Back"}
            </Button>
          </CardContent>
        </Card>

        {/* Footer */}
        <div className="text-center text-sm text-muted-foreground">
          <p>© {new Date().getFullYear()} Goodacres Residential Limited</p>
        </div>
      </div>
    </div>
  );
};

export default BookInspectionPage;
